import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, KeyRound } from 'lucide-react';
import { employeeApi, errMsg } from '../api';
import { WorkforceAuthPage, WorkforceHero, PasswordInput } from '../WorkforceTheme';

export default function EmployeeReset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    employeeApi.linkInfo(token).then((r) => setInfo(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const mismatch = passwordConfirm && password !== passwordConfirm;

  const submit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) return setError('Passwords do not match');
    setBusy(true); setError('');
    try {
      const r = await employeeApi.reset({ token, email: email || null, password });
      localStorage.setItem('employee_token', r.data.token);
      navigate('/workforce/home');
    } catch (err) {
      setError(errMsg(err, 'Could not reset your login'));
    } finally { setBusy(false); }
  };

  if (invalid) return (
    <WorkforceAuthPage showStrip={false}>
      <WorkforceHero />
      <p className="text-gray-300 mt-2 max-w-sm text-center" data-testid="reset-invalid">This reset link is no longer valid. Ask BuildBroz to send you a new one.</p>
    </WorkforceAuthPage>
  );
  if (!info) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div>;

  return (
    <WorkforceAuthPage showStrip={false}>
      <div className="w-full max-w-2xl">
        <WorkforceHero subtitle={`Hi ${info.name} — set a new password. All your work history and pay stubs are preserved.`} />
        <form onSubmit={submit} className="w-full max-w-sm mx-auto rounded-2xl border border-[#D4AF37]/25 bg-black/60 backdrop-blur-md p-6 space-y-4 shadow-[0_0_60px_rgba(212,175,55,0.08)]" data-testid="workforce-reset-form">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-full border border-[#D4AF37]/60 flex items-center justify-center"><KeyRound size={14} className="text-[#D4AF37]" /></span>
            <h1 className="text-white font-brand font-extrabold text-lg">Reset Your Login</h1>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1.5">New Login Email (optional)</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoCapitalize="none" placeholder="Leave blank to keep current" className="bg-black/60 border-gray-700 text-white" data-testid="reset-email" />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1.5">New Password * (at least 6 characters)</label>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} testId="reset-password" />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1.5">Re-enter New Password *</label>
            <PasswordInput value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} testId="reset-password-confirm" />
            {mismatch && <p className="text-red-400 text-xs mt-1" data-testid="reset-password-mismatch">Passwords do not match</p>}
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="reset-error">{error}</p>}
          <Button type="submit" disabled={busy || password.length < 6 || !passwordConfirm} className="w-full bg-gradient-to-b from-[#F0CB5C] to-[#C99E2E] text-black hover:from-[#D4AF37] hover:to-[#B8941F] font-brand font-extrabold uppercase tracking-wide py-6" data-testid="reset-submit-btn">
            {busy && <Loader2 size={16} className="animate-spin" />} Save &amp; Sign In
          </Button>
        </form>
      </div>
    </WorkforceAuthPage>
  );
}
